import { useMemo } from "react";
import { resolveAppearance } from "@picnic-field/renderer/appearance";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { boardKind } from "../../../lib/api/fieldApi";
import type { BenchSource, CampusSource } from "../../../lib/api/fieldApi";
import { Button } from "../../../components/ui/Button";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { STATIC_TEXT } from "../../../lib/staticReason";
import { isFs02Code } from "../lib/checks";
import { formatWhen } from "../lib/format";
import { lessonTitle, modelName } from "../lib/titles";

/**
 * FS-02-01 작품 올리기 — 선택. ① Campus 조립(칠한 색 · 받은 날) ② Bench 프로젝트(보드 · 저장 시각).
 * 선택 요소는 버튼(role=radio)뿐 — 자유 입력 0(DR-02). 만료된 가져오기는 '다시 가져오기'.
 * micro:bit 는 '움직이지 않는 전시' 칩(SR-RUN-07).
 * 전시만 · micro:bit 를 고르면 왜 움직이지 않는지와 달리려면 무엇을 할지 한 줄(💡 — 정적 안내).
 */
export interface PickPaneProps {
  imports: readonly CampusSource[];
  projects: readonly BenchSource[];
  palette: PaletteLike | null;
  importId: string | null;
  /** null = 코딩 없이 전시만 */
  projectId: string | null;
  refreshing: string | null;
  onImport: (id: string) => void;
  onProject: (id: string | null) => void;
  onRefresh: (id: string) => void;
  onNext: () => void;
}

const BOARD_ICON = { spike: "🟨", microbit: "🟥", other: "▫" } as const;
/** 보드 칩 이름 — board 'spike'(내부 id)는 제품 이름 대신 '스마트 허브'(i18n · 상표 정책 2026-09-30) */
const BOARD_NAME = {
  microbit: "micro:bit",
  other: "",
} as const;

/**
 * 조립 썸네일 — Campus 썸네일(색 저장본, 선택 필드 thumbnailUrl)이 있으면 그것, 없으면 와이어의 줄무늬 자리에
 * 칠한 색 견본을 얹는다.
 */
function Thumb({ imp, palette }: { imp: CampusSource; palette: PaletteLike | null }) {
  return (
    <span
      className="pf-pick__thumb"
      aria-hidden="true"
      data-thumb={imp.thumbnailUrl ? "campus" : "swatch"}
    >
      {imp.thumbnailUrl ? (
        <img src={imp.thumbnailUrl} alt="" loading="lazy" />
      ) : (
        <Swatches imp={imp} palette={palette} />
      )}
    </span>
  );
}

function Swatches({ imp, palette }: { imp: CampusSource; palette: PaletteLike | null }) {
  const colors = useMemo(() => {
    const r = resolveAppearance(imp.appearance, palette);
    return [...new Set(r.colors.values())].slice(0, 5);
  }, [imp.appearance, palette]);
  return (
    <span className="pf-swatches" aria-hidden="true">
      {colors.length === 0 ? (
        <span className="pf-swatches__glyph">🧩</span>
      ) : (
        colors.map((c) => <i key={c} style={{ background: c }} />)
      )}
    </span>
  );
}

export function PickPane(p: PickPaneProps) {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const canNext = !!p.importId;
  // 고른 조립이 스파이크 프라임 판이면 프로그램 칩도 그 교구 이름으로(2026-11-03 — 프라임 코스에서도 '스마트 허브' 로 나왔다)
  const prime = p.imports.find((x) => x.importId === p.importId)?.variant === "prime";
  // 고른 것이 움직이지 않는 전시가 되는 이유와 다음 행동(전시만 · micro:bit) — 달리려면 내 SPIKE 프로그램이 있어야 한다
  const picked = p.projects.find((x) => x.projectId === p.projectId) ?? null;
  const hasRunnable = p.projects.some((x) => boardKind(x.board) === "spike" && !!x.versionHash);
  const staticHint =
    p.projectId === null
      ? {
          reason: "noProgram",
          key: hasRunnable ? "student.upload.displayOnlyHint" : STATIC_TEXT.noProgram.next,
        }
      : picked && boardKind(picked.board) === "microbit"
        ? { reason: "microbit", key: STATIC_TEXT.microbit.short }
        : null;

  return (
    <div className="pf-upload__pick">
      <section className="pf-upload__col" aria-labelledby="pick-import">
        <h2 id="pick-import" className="pf-h3">
          {t("student.upload.pickImport")}
        </h2>
        {p.imports.length === 0 ? (
          <p className="pf-muted" data-testid="no-imports">
            {t("student.upload.noImports")}
          </p>
        ) : (
          <div
            className={`pf-picks pf-picks--imports pf-picks--${density}`}
            role="radiogroup"
            aria-labelledby="pick-import"
          >
            {p.imports.map((imp) => {
              const expired = imp.status === "expired";
              const on = p.importId === imp.importId;
              return (
                <div
                  key={imp.importId}
                  className={`pf-pick-card${on ? " pf-pick-card--on" : ""}${expired ? " pf-pick-card--expired" : ""}`}
                  data-import={imp.importId}
                  data-status={imp.status}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-disabled={expired || undefined}
                    className="pf-pick"
                    onClick={() => !expired && p.onImport(imp.importId)}
                  >
                    <Thumb imp={imp} palette={p.palette} />
                    <span className="pf-pick__title">
                      {lessonTitle(t, locale, imp.lesson) ??
                        modelName(t, locale, imp.modelSlug, imp.title)}
                    </span>
                    {imp.lesson?.no != null && (
                      <span className="pf-pick__meta" data-testid="import-lesson">
                        {t("student.lesson.no", { no: imp.lesson.no })}
                      </span>
                    )}
                    {(!se || imp.lesson?.no == null) && (
                      <span className="pf-pick__meta">
                        {t("student.upload.receivedAt", {
                          date: formatWhen(imp.receivedAt, locale, !se),
                        })}
                      </span>
                    )}
                  </button>
                  {!expired && imp.feCode && isFs02Code(imp.feCode) && (
                    <p className="pf-muted pf-pick-card__note" data-fe-code={imp.feCode}>
                      {t(`fe.${imp.feCode}.student.next`)}
                    </p>
                  )}
                  {expired && (
                    <div className="pf-pick-card__expired">
                      <p className="pf-muted">{t("student.upload.expired")}</p>
                      <Button
                        variant="ghost"
                        icon="↻"
                        disabled={p.refreshing === imp.importId}
                        onClick={() => p.onRefresh(imp.importId)}
                      >
                        {p.refreshing === imp.importId
                          ? t("student.upload.refreshing")
                          : t("student.upload.refresh")}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="pf-upload__col" aria-labelledby="pick-project">
        <h2 id="pick-project" className="pf-h3">
          {t("student.upload.pickProject")}
        </h2>
        {p.projects.length === 0 && <p className="pf-muted">{t("student.upload.noProjects")}</p>}
        <div
          className="pf-picks pf-picks--projects"
          role="radiogroup"
          aria-labelledby="pick-project"
        >
          {p.projects.map((prj) => {
            const kind = boardKind(prj.board);
            const on = p.projectId === prj.projectId;
            return (
              <button
                key={prj.projectId}
                type="button"
                role="radio"
                aria-checked={on}
                className={`pf-pick pf-pick--row${on ? " pf-pick--on" : ""}`}
                data-project={prj.projectId}
                data-board={kind}
                onClick={() => p.onProject(prj.projectId)}
              >
                <span className="pf-pick__icon" aria-hidden="true">
                  {BOARD_ICON[kind]}
                </span>
                <span className="pf-pick__title">{prj.title}</span>
                {!se && (
                  <span className="pf-pick__meta">
                    {t("student.upload.savedAt", { date: formatWhen(prj.updatedAt, locale, true) })}
                  </span>
                )}
                <span
                  className={`pf-chip-static ${kind === "microbit" ? "pf-chip-static--run" : "pf-chip-static--ok"}`}
                >
                  {kind === "spike" ? t(prime ? "student.upload.boardSpikePrime" : "student.upload.boardSmartHub") : BOARD_NAME[kind]}
                  {kind === "microbit" && ` · ${t("student.upload.staticChip")}`}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            role="radio"
            aria-checked={p.projectId === null}
            className={`pf-pick pf-pick--row pf-pick--dashed${p.projectId === null ? " pf-pick--on" : ""}`}
            data-project="display-only"
            onClick={() => p.onProject(null)}
          >
            <span className="pf-pick__icon" aria-hidden="true">
              🖼
            </span>
            <span className="pf-pick__title">{t("student.upload.displayOnly")}</span>
            <span
              className={`pf-pick__select${p.projectId === null ? " pf-pick__select--on" : ""}`}
              aria-hidden="true"
            >
              {p.projectId === null ? "✓" : t("student.upload.select")}
            </span>
          </button>
        </div>
        {staticHint && (
          <p
            className="pf-banner pf-banner--info pf-upload__note"
            data-testid="static-hint"
            data-reason={staticHint.reason}
          >
            <span className="pf-banner__icon" aria-hidden="true">
              💡
            </span>
            <span className="pf-banner__text">{t(staticHint.key)}</span>
          </p>
        )}
      </section>

      <div className="pf-upload__actions">
        <Button variant="accent" icon="→" disabled={!canNext} onClick={p.onNext}>
          {t("student.upload.next")}
        </Button>
      </div>
    </div>
  );
}
